import { Download } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatAmountMinor, resolveFinanceLocale } from '../finance-format';
import { type ProducedRegister, type StatementFormatCheck } from './prima-nota-model';

/**
 * What a produced prima nota gives you at once: the file, its size in rows, and whether it is ready
 * to hand over. The download is the one primary action once a prima nota exists.
 */
export function ProducedSummary({
  register,
  onDownload,
  onShowFindings,
}: {
  register: ProducedRegister;
  onDownload: () => void;
  onShowFindings: () => void;
}) {
  const { t } = useTranslation('finance');
  return (
    <section
      aria-labelledby="prima-nota-produced-heading"
      className="border-border bg-card space-y-3 rounded-lg border p-4"
      data-testid="prima-nota-produced"
    >
      <h3 className="sr-only" id="prima-nota-produced-heading">
        {t('primaNota.result.summaryHeading')}
      </h3>
      <Button className="w-full" onClick={onDownload} data-testid="prima-nota-download">
        <Download aria-hidden="true" />
        {t('primaNota.generate.download')}
      </Button>
      <p className="text-muted-foreground text-center text-[13px] leading-5">
        <span className="text-foreground font-medium break-all">{register.filename}</span>
        <br />
        {t('primaNota.generate.rowCount', { count: register.receipt.rowCount })}
      </p>
      <p
        className={
          register.vouchable
            ? 'bg-success-tint text-success rounded-md px-3 py-2 text-sm leading-6'
            : 'bg-warning-tint text-warning rounded-md px-3 py-2 text-sm leading-6'
        }
      >
        {register.vouchable ? (
          t('primaNota.generate.vouchable')
        ) : (
          <>
            {t('primaNota.generate.notVouchableLead')}{' '}
            <a
              href="#prima-nota-findings"
              className="font-semibold underline underline-offset-2"
              data-testid="prima-nota-see-findings"
              onClick={(event) => {
                event.preventDefault();
                onShowFindings();
              }}
            >
              {t('primaNota.generate.seeFindings')}
            </a>
            .
          </>
        )}
      </p>
    </section>
  );
}

/**
 * How each account's PDF and CSV compared in this run, and which the matching read.
 *
 * Warnings only (decision 71: a disagreement is «Solo un avviso»): they never make the prima nota
 * unvouchable, because what a disagreement costs is counted where it lands — an unreconciled row,
 * an open item above. Kept in the findings, below the download, so it never pushes the download
 * out of reach.
 */
export function StatementFormatsSection({
  checks,
  labelOf,
}: {
  checks: readonly StatementFormatCheck[];
  labelOf: (accountKey: string) => string;
}) {
  const { t, i18n } = useTranslation('finance');
  const locale = resolveFinanceLocale(i18n.resolvedLanguage);
  // The books' calendar day, written as the rest of the page writes dates; noon UTC is the same
  // day in Rome, so a day given as a date never shifts.
  const day = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'it', {
        dateStyle: 'short',
        timeZone: 'Europe/Rome',
      }),
    [i18n.resolvedLanguage],
  );
  if (checks.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2" data-testid="prima-nota-statement-formats">
      <h4 className="card-label">{t('primaNota.formats.heading')}</h4>
      <ul className="space-y-2 text-sm">
        {checks.map((check) => (
          <li key={check.accountKey} data-testid={`statement-format-${check.accountKey}`}>
            {check.outcome === 'agree' ? (
              <p className="leading-6">
                <span className="font-semibold">{labelOf(check.accountKey)}</span>
                {' — '}
                {t('primaNota.formats.agree', { count: check.paired })}
                {check.fromPdfOutsideCsv > 0 &&
                  ` ${t('primaNota.formats.outside', { count: check.fromPdfOutsideCsv })}`}
              </p>
            ) : (
              <div className="bg-warning-tint text-warning space-y-1 rounded-md px-3 py-2 leading-6">
                <p>
                  <span className="font-semibold">{labelOf(check.accountKey)}</span>
                  {' — '}
                  {t(`primaNota.formats.${check.outcome}`)}
                </p>
                {check.outcome === 'disagree' && (
                  <ul className="space-y-0.5 text-[13px]">
                    {[
                      ...check.onlyInCsv.map((movement) => ({ ...movement, side: 'csv' as const })),
                      ...check.onlyInPdf.map((movement) => ({ ...movement, side: 'pdf' as const })),
                    ].map((movement, index) => (
                      <li
                        key={`${movement.side}:${movement.date ?? ''}:${movement.amountMinor}:${index}`}
                        className="break-words"
                      >
                        {movement.side === 'csv'
                          ? t('primaNota.formats.onlyInCsv', { file: movement.file })
                          : t('primaNota.formats.onlyInPdf', { file: movement.file })}
                        {': '}
                        {movement.date === null
                          ? '—'
                          : day.format(new Date(`${movement.date}T12:00:00Z`))}{' '}
                        · {formatAmountMinor(movement.amountMinor, locale)} · {movement.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
